import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { apiClient } from "@/lib/client";
import { ENDPOINTS } from "@/api/endpoints";

interface RevenueDistribution {
  occasion: Record<string, number>;
  colour: Record<string, number>;
  articleType: Record<string, number>;
  season: Record<string, number>;
  brand: Record<string, number>;
}

interface GetRevenueDistributionParams {
  jobId: string;
  date: string;
}

interface TransformedRevenueDistribution {
  occasion: Array<{ type: string; value: number }>;
  colour: Array<{ type: string; value: number }>;
  articleType: Array<{ type: string; value: number }>;
  season: Array<{ type: string; value: number }>;
  brand: Array<{ type: string; value: number }>;
}

const getRevenueDistribution = async ({
  jobId,
  date,
}: GetRevenueDistributionParams): Promise<RevenueDistribution> => {
  return apiClient
    .get(ENDPOINTS.GET_INSIGHTS_REVENUE_DISTRIBUTION(jobId, date))
    .json<RevenueDistribution>();
};
const transformData = (data: Record<string, number>) =>
  Object.entries(data)
    .sort(([, a], [, b]) => b - a)
    .reduce((acc, [type, value], index) => {
      if (index < 6) {
        acc.push({
          type,
          value: Number(value.toFixed(2)),
        });
      } else if (acc[6]) {
        acc[6].value = Number((acc[6].value + Number(value)).toFixed(2));
      } else {
        acc.push({
          type: "Other",
          value: Number(value.toFixed(2)),
        });
      }
      return acc;
    }, [] as { type: string; value: number }[]);

export const useGetRevenueDistributionQuery = (
  params: GetRevenueDistributionParams,
  options?: Omit<
    UseQueryOptions<RevenueDistribution, Error, TransformedRevenueDistribution>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["revenueDistribution", params.jobId, params.date],
    queryFn: () => getRevenueDistribution(params),
    select: (data) => ({
      occasion: transformData(data.occasion),
      colour: transformData(data.colour),
      articleType: transformData(data.articleType),
      season: transformData(data.season),
      brand: transformData(data.brand),
    }),
    ...options,
  });
};
